import { format, parseISO } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import {
  ExternalCourseEnrolled,
  Evaluation,
} from 'models/externalCoursesEnrolled.model'
import { State } from 'components/ui/externalCourses'
import { IMainState } from 'store/states'
import { joinKeys } from 'helpers/translations.helper'
import { useMemo } from 'react'

export function useColumns() {
  const { t } = useTranslation()
  type Column = {
    title: string
    dataIndex?: string
    key:
      | keyof ExternalCourseEnrolled
      | 'external_course_name'
      | `evaluations[${number}]`
      | 'action'
    sorteable?: boolean
    width?: number
    render?: (data: any, all: ExternalCourseEnrolled) => any
  }

  const list = useSelector(
    ({ externalCoursesEnrolled }: IMainState) => externalCoursesEnrolled.list
  )

  const formatDate = (date?: string) => {
    if (date) {
      try {
        return format(parseISO(date), 'dd/MM/yyyy')
      } catch {
        return ''
      }
    }
    return ''
  }
  const prefixBase = 'administration.externalCourses'
  const prefix = joinKeys(prefixBase, 'attributes')
  const prefixParticipants = joinKeys(prefixBase, 'participants.columns')

  const evaluationsMax = Math.max(
    ...list.map(({ evaluations }) => evaluations.length || 0),
    0
  )

  const evaluationsColumns = [...new Array(evaluationsMax)].map(
    (_, i): Column => {
      return {
        title: t(`${prefixParticipants}.evaluation`, { name: i + 1 }),
        key: `evaluations[${i}]`,
        width: 100,
        sorteable: false,
        render: (evaluation?: Evaluation) => {
          if (!evaluation) return ''
          const { points, maxPoints } = evaluation
          return `${points}/${maxPoints}`
        },
      }
    }
  )

  const columns: Column[] = useMemo(
    () => [
      {
        title: t(`${prefix}.name`),
        key: 'external_course_name',
        sorteable: true,
        render: (_, { externalCourse }) => {
          return (
            <div>
              {externalCourse.name}{' '}
              <span style={{ color: '#aaa9a9' }}>({externalCourse.code})</span>
            </div>
          )
        },
      },
      {
        title: t(`${prefixParticipants}.state`),
        key: 'state',
        width: 135,
        sorteable: true,
        render: (state: number) => <State state={state} />,
      },
      {
        title: t(`${prefixParticipants}.attendance`),
        key: 'attendance',
        width: 110,
        sorteable: true,
        render: (attendance: number) => Number(attendance) + '%',
      },
      {
        title: t(`${prefixParticipants}.totalHours`),
        key: 'totalHours',
        width: 100,
        sorteable: true,
        render: (_, { totalHours }) => parseFloat(totalHours),
      },
      ...evaluationsColumns,
      {
        title: t(`${prefix}.startDate`),
        key: 'startDate',
        width: 120,
        sorteable: true,
        render: (date) => formatDate(date),
      },
      {
        title: t(`${prefix}.endDate`),
        key: 'endDate',
        width: 140,
        sorteable: true,
        render: (date) => formatDate(date),
      },
    ],
    [evaluationsColumns, prefix, prefixParticipants, t]
  )
  return columns
}
